import { useEffect, useState } from 'react'
import { ClubButton } from './ClubButton'

interface CardPanelProps {
  open: boolean
  onClose: () => void
  /** Phase 1→2: POST /club/setup_intent, resolving the Elements client secret. */
  onCreateIntent: () => Promise<{ client_secret: string }>
  /**
   * Phase-2 slot. The child owns the Elements provider, so this panel — and the
   * screen that renders it — never import @stripe/*.
   */
  children: (ctx: { clientSecret: string; onBack: () => void }) => React.ReactNode
}

/**
 * Where a card is lodged, on the page that asked for it.
 *
 * An inline brass-ruled panel rather than a modal, for two reasons: the golf
 * system has no Dialog and `components/ui/Dialog` is forbidden to club screens,
 * and the disclosure reads better directly beneath the notice that sent the
 * member here.
 *
 * Two phases, like TopUpDialog: the intent is created only when the panel
 * opens, so a member who never asks for a card never mints one at Stripe.
 */
export function CardPanel({ open, onClose, onCreateIntent, children }: CardPanelProps) {
  const [clientSecret, setClientSecret] = useState('')
  const [error, setError] = useState('')

  // Closing forgets the intent so the next open mints a fresh one. Done while
  // rendering (guarded by the previous value, per the React docs) rather than
  // in the effect, which must not set state synchronously.
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) {
      setClientSecret('')
      setError('')
    }
  }

  // The desk is opening exactly while the panel is open with nothing to show
  // yet — derived, not a state flag the effect would have to flip.
  const loading = open && !clientSecret && !error

  useEffect(() => {
    if (!open) return
    let active = true
    onCreateIntent()
      .then((result) => {
        if (active) setClientSecret(result.client_secret)
      })
      .catch((caught: unknown) => {
        if (active) {
          setError(caught instanceof Error && caught.message ? caught.message : 'We couldn’t open the desk')
        }
      })
    return () => {
      active = false
    }
    // onCreateIntent is recreated each render by the screen; depending on it
    // would re-open the intent on every keystroke elsewhere on the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  return (
    <section className="mt-9 rounded-club-lg border border-brass bg-paper px-6 py-5">
      <h3 className="font-display text-[17px] font-medium leading-tight text-pine">
        Lodge a card with the club.
      </h3>
      <p className="mb-5 mt-1.5 max-w-[56ch] text-[13.5px] text-ink-soft">
        Taken by Stripe inside this page — the club never sees or holds a card number.
      </p>

      {loading && <p className="text-[13.5px] text-ink-soft">Opening the desk&hellip;</p>}

      {error && (
        <div className="space-y-4">
          <p role="alert" className="text-[13.5px] text-flag">
            {error}
          </p>
          <ClubButton variant="ghost" size="sm" onClick={onClose}>
            Back
          </ClubButton>
        </div>
      )}

      {clientSecret && !error && children({ clientSecret, onBack: onClose })}
    </section>
  )
}
